import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/Brand";
import { ShieldStar, QrCode, IdentificationCard, Translate, Lock, EnvelopeSimple, Phone, MapPin } from "@phosphor-icons/react";
import LandingWalkthrough from "@/components/LandingWalkthrough";

export default function Landing() {
    const { t, toggle, lang, nextLangLabel } = useI18n();
    return (
        <div className="min-h-screen bg-[#101012] text-white relative overflow-hidden">
            <div className="grain" />
            <div
                className="absolute inset-0 opacity-25 pointer-events-none"
                style={{
                    backgroundImage: "url(https://images.unsplash.com/photo-1545987796-200677ee1011?crop=entropy&cs=srgb&fm=jpg&w=1600)",
                    backgroundSize: "cover", backgroundPosition: "center",
                    maskImage: "linear-gradient(to bottom, black 20%, transparent 90%)",
                }}
            />
            <header className="relative z-10 px-6 sm:px-10 py-6 flex items-center justify-between">
                <Brand to="https://utal.com/en/" variant="dark" size="md" tagline="MULTI-JURISDICTION" taglineColor="text-white/80" dataTestId="landing-brand" />
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={toggle} className="text-white hover:bg-white/10" data-testid="landing-lang-toggle">
                        <Translate size={16} weight="bold" className="me-1" />{nextLangLabel()}
                    </Button>
                    <Link to="/login"><Button size="sm" className="bg-primary hover:bg-primary/90" data-testid="landing-signin-btn">
                        <Lock size={16} weight="bold" className="me-1" />{t("signIn")}
                    </Button></Link>
                </div>
            </header>

            <section className="relative z-10 px-6 sm:px-10 pt-16 sm:pt-24 pb-20 max-w-6xl">
                <div className="text-[11px] tracking-[0.4em] uppercase text-red-300 font-bold mb-6" style={{ textShadow: "0 0 24px rgba(248,113,113,0.55)" }} data-testid="landing-eyebrow">
                    · Government-grade · Ed25519 signed
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-tight max-w-4xl" data-testid="landing-title">
                    {t("tagline")}
                </h1>
                <p className="mt-6 text-white/70 text-base sm:text-lg max-w-2xl leading-relaxed">
                    {t("subtagline")} — {lang === "ar"
                        ? "منصة موحّدة لتسجيل المركبات، إدارة الوثائق، طباعة الملصقات الآمنة، والتحقق الميداني بمسح رمز QR."
                        : lang === "fr"
                        ? "Une plateforme unifiée pour l'immatriculation des véhicules, le workflow documentaire, l'impression sécurisée des vignettes et la vérification QR sur le terrain."
                        : "A unified platform for vehicle registration, document workflow, secure sticker printing, and field QR verification."}
                </p>
                <div className="mt-10 flex flex-wrap gap-3">
                    <Link to="/login"><Button size="lg" className="bg-primary hover:bg-primary/90 h-12 px-6 rounded-sm" data-testid="landing-cta-portal">
                        {t("signIn")}
                    </Button></Link>
                </div>

                <div className="mt-24 grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-4xl">
                    {[
                        { icon: IdentificationCard, k: "01 · Registration", d: lang === "ar" ? "معالجة الطلبات وسير العمل" : lang === "fr" ? "Demandes & workflow" : "Applications & workflow" },
                        { icon: ShieldStar, k: "02 · Signing", d: lang === "ar" ? "توقيع Ed25519 مع تدوير المفاتيح" : lang === "fr" ? "Signature Ed25519 · rotation des clés" : "Ed25519 signing · key rotation" },
                        { icon: QrCode, k: "03 · Enforcement", d: lang === "ar" ? "مسح ميداني آمن أونلاين/أوفلاين" : lang === "fr" ? "Scan de terrain sécurisé en/hors ligne" : "Online/offline field scan" },
                    ].map(({ icon: Icon, k, d }) => (
                        <div key={k} className="bg-[#101012] p-6">
                            <Icon size={28} weight="duotone" className="text-primary mb-4" />
                            <div className="text-[10px] tracking-[0.3em] text-red-300 font-bold mb-2">{k}</div>
                            <div className="text-sm text-white/80">{d}</div>
                        </div>
                    ))}
                </div>
            </section>

            <LandingWalkthrough />

            {/* Footer — contact + copyright + disclosure */}
            <footer className="relative z-10 border-t border-white/10 mt-24" data-testid="landing-footer">
                <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16 grid gap-12 lg:grid-cols-3">
                    <div>
                        <Brand to="https://utal.com/en/" size="md" variant="dark" dataTestId="footer-brand" />
                        <p className="mt-4 text-white/60 text-sm max-w-sm leading-relaxed">
                            {lang === "ar"
                                ? "منصة تسجيل وتحقق مركزية للحكومات ومشغّلي أساطيل المركبات."
                                : lang === "fr"
                                ? "Plateforme centralisée d'immatriculation et de vérification pour les gouvernements et les flottes."
                                : "Centralised vehicle registration & verification platform for governments and fleet operators."}
                        </p>
                    </div>

                    <div>
                        <div className="text-[10px] tracking-[0.35em] uppercase text-primary mb-4 font-bold">· Offices</div>
                        <ul className="space-y-2.5 text-sm text-white/75">
                            <li className="flex items-start gap-2.5" data-testid="footer-office-dubai">
                                <MapPin size={14} weight="fill" className="text-primary mt-1 shrink-0" />
                                <span>Dubai, United Arab Emirates <span className="text-white/40">· HQ</span></span>
                            </li>
                            <li className="flex items-start gap-2.5" data-testid="footer-office-beirut">
                                <MapPin size={14} weight="fill" className="text-primary mt-1 shrink-0" />
                                <span>Beirut, Lebanon</span>
                            </li>
                            <li className="flex items-start gap-2.5" data-testid="footer-office-montreal">
                                <MapPin size={14} weight="fill" className="text-primary mt-1 shrink-0" />
                                <span>Montreal, Canada</span>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <div className="text-[10px] tracking-[0.35em] uppercase text-primary mb-4 font-bold">· Contact</div>
                        <ul className="space-y-2.5 text-sm text-white/75">
                            <li>
                                <a href="mailto:mm@utal.me" className="flex items-center gap-2.5 hover:text-white transition-colors" data-testid="footer-email">
                                    <EnvelopeSimple size={14} weight="fill" className="text-primary shrink-0" />
                                    <span className="mono">mm@utal.me</span>
                                </a>
                            </li>
                            <li>
                                <a href="tel:+971505863020" className="flex items-center gap-2.5 hover:text-white transition-colors" data-testid="footer-phone">
                                    <Phone size={14} weight="fill" className="text-primary shrink-0" />
                                    <span className="mono">+971 50 586 3020</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="border-t border-white/10">
                    <div className="max-w-7xl mx-auto px-6 sm:px-12 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="text-[11px] text-white/50 mono" data-testid="footer-copyright">
                            © {new Date().getFullYear()} UTAL MEA LLC FZ · All rights reserved.
                        </div>
                        <p className="text-[11px] text-white/40 max-w-3xl leading-relaxed" data-testid="footer-disclosure">
                            {lang === "ar"
                                ? "منصة UTAL MEA مخصّصة للاستخدام الحكومي المرخّص وعمليات إنفاذ القانون. تعمل كافة العمليات وفق ضوابط مراجعة صارمة وقنوات تشفير موقّعة. تُوفّر UTAL MEA LLC FZ حقوق الملكية الكاملة على البرنامج والعلامات التجارية والبنية المشفّرة."
                                : lang === "fr"
                                ? "La plateforme UTAL MEA est destinée à un usage gouvernemental et aux forces de l'ordre autorisés. Toutes les opérations sont auditées et signées par cryptographie Ed25519. UTAL MEA LLC FZ conserve l'ensemble des droits sur le logiciel, les marques et l'infrastructure cryptographique."
                                : "UTAL MEA is a licensed government and enforcement platform. All operations are cryptographically signed (Ed25519) and fully audited. UTAL MEA LLC FZ retains all rights to the software, trademarks, and cryptographic infrastructure."}
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
